import React, { ReactNode } from "react";
import setHtml from "~/helpers/setHtml";
import Tooltip from "~/components/Tooltip";
import LegalAnnotation from "~/components/LegalAnnotation";
import InfoCircle from "~/components/Icons/outline/InfoCircle";

interface InjectLegalAndTooltipsProps {
  copy: string | ReactNode;
}

/**
 * @component InjectLegalAndTooltips
 * @description A React component that renders text with legal annotations and tooltips
 * Example input: "No throttling or data caps {data-caps} plus premium eero router included[eero]"
 */
const InjectLegalAndTooltips: React.FC<InjectLegalAndTooltipsProps> = ({ copy }) => {
  if (typeof copy !== "string") return <>{copy}</>;

  // Split the text by both legal annotations [like this] and tooltips {like this}
  const parts = copy.split(/(\[.*?\]|\{.*?\})/g).filter(Boolean);

  const renderedLegalAndTooltips = parts.map((part, index) => {
    if (part.startsWith("[") && part.endsWith("]")) {
      const legalKey = part.slice(1, -1).trim();
      return <LegalAnnotation key={`legal-${legalKey}`} legalKey={legalKey} />;
    }

    if (part.startsWith("{") && part.endsWith("}")) {
      const tooltipHash = part.slice(1, -1).trim();
      return (
        <Tooltip
          key={`tooltip-${tooltipHash}`}
          interactive
          position="top"
          theme="light"
          html={
            <div style={{ width: 240 }}>
              <div className="tooltip-info">
                <span {...setHtml(tooltipHash)} />
              </div>
            </div>
          }
        >
          <InfoCircle />
        </Tooltip>
      );
    }

    return <span key={`text-${index}`} {...setHtml(part)} />;
  });

  return <>{renderedLegalAndTooltips}</>;
};

InjectLegalAndTooltips.displayName = "InjectLegalAndTooltips";

export default InjectLegalAndTooltips;
